import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, MessageRole, NodeDevice } from '../types';
import { sendChatMessage } from '../services/geminiService';

interface ChatWidgetProps {
    onAction: (type: 'CREATE' | 'UPDATE' | 'DELETE', data: any) => void;
    nodes: NodeDevice[];
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ onAction, nodes }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            id: '1',
            role: MessageRole.MODEL,
            text: 'Hola, soy el Agente Axend. ¿Qué cambios necesitas realizar en la base de datos hoy? Ahora puedo sincronizar borrados por ID numérico.',
            timestamp: new Date()
        }
    ]);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) scrollToBottom();
    }, [messages, isOpen]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMsg: ChatMessage = {
            id: Date.now().toString(),
            role: MessageRole.USER,
            text: input,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsTyping(true);

        const history = messages.map(m => ({
            role: m.role === MessageRole.USER ? "user" : "model",
            parts: [{ text: m.text }]
        }));

        try {
            // Pasamos los nodos actuales como contexto a Gemini
            const response = await sendChatMessage(userMsg.text, history, nodes);
            
            if (response.functionCalls && response.functionCalls.length > 0) {
                response.functionCalls.forEach((fc: any) => {
                    console.log("Exec Tool:", fc.name, fc.args);
                    if (fc.name === 'create_camera') {
                        onAction('CREATE', fc.args.cameras);
                    } else if (fc.name === 'update_camera') {
                        onAction('UPDATE', { id: fc.args.id, updates: fc.args.updates });
                    } else if (fc.name === 'delete_camera') {
                        // El modelo ahora debería proveer el ID numérico
                        onAction('DELETE', fc.args.id);
                    }
                });
            }

            const modelMsg: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: MessageRole.MODEL,
                text: response.text || "Operación procesada. He sincronizado los cambios con la base de datos.",
                timestamp: new Date()
            };
            setMessages(prev => [...prev, modelMsg]);
        } catch (e) {
            console.error(e);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end pointer-events-none">
            <div className={`pointer-events-auto glass border border-[#30363d] rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.5)] w-[400px] mb-4 overflow-hidden transition-all duration-500 origin-bottom-right ${isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-10 pointer-events-none h-0'}`}>
                <div className="bg-[#0d1117] p-5 border-b border-[#30363d] flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20">
                            <span className="material-icons-round text-primary text-xl animate-pulse">terminal</span>
                        </div>
                        <div>
                            <h3 className="text-white font-black text-sm uppercase tracking-widest">IA Agent Sync</h3>
                            <p className="text-[10px] text-gray-500 font-bold uppercase flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span> {nodes.length} Nodos en Contexto
                            </p>
                        </div>
                    </div>
                    <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-white transition-colors">
                        <span className="material-icons-round">close</span>
                    </button>
                </div>

                <div className="h-[400px] overflow-y-auto p-5 space-y-5 bg-[#0d1117]/50 custom-scrollbar">
                    {messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.role === MessageRole.USER ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-xs shadow-xl leading-relaxed ${
                                msg.role === MessageRole.USER 
                                ? 'bg-primary text-[#0d1117] font-bold rounded-tr-none' 
                                : 'bg-[#161b22] text-gray-200 rounded-tl-none border border-[#30363d]'
                            }`}>
                                <p className="whitespace-pre-wrap">{msg.text}</p>
                                <span className="text-[9px] opacity-40 block mt-2 font-mono">
                                    {msg.timestamp.toLocaleTimeString()}
                                </span>
                            </div>
                        </div>
                    ))}
                    {isTyping && (
                        <div className="flex justify-start">
                             <div className="bg-[#161b22] rounded-2xl rounded-tl-none px-4 py-2 border border-[#30363d] flex gap-1.5">
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce"></span>
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce delay-150"></span>
                                <span className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce delay-300"></span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div className="p-5 bg-[#0d1117]">
                    <div className="flex items-center gap-3 bg-[#161b22] rounded-2xl px-4 py-3 border border-[#30363d] focus-within:border-primary/50 transition-all">
                        <input 
                            type="text" 
                            className="flex-grow bg-transparent border-none focus:ring-0 text-xs text-white placeholder-gray-600 font-medium"
                            placeholder="Ej: Borra todas las MC-TEST..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyPress}
                        />
                        <button 
                            onClick={handleSend}
                            disabled={!input.trim() || isTyping}
                            className="w-8 h-8 rounded-xl bg-primary text-[#0d1117] hover:scale-110 active:scale-95 transition-all disabled:opacity-20 flex items-center justify-center shadow-lg shadow-primary/10"
                        >
                            <span className="material-icons-round text-lg font-bold">send</span>
                        </button>
                    </div>
                </div>
            </div>

            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="pointer-events-auto w-16 h-16 rounded-2xl bg-[#0d1117] border border-[#30363d] hover:border-primary/50 text-primary shadow-2xl flex items-center justify-center transition-all group active:scale-90"
            >
                <div className="relative">
                    <span className="material-icons-round text-3xl group-hover:rotate-12 transition-transform">{isOpen ? 'keyboard_arrow_down' : 'smart_toy'}</span>
                    {!isOpen && <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-ping"></span>}
                </div>
            </button>
        </div>
    );
};

export default ChatWidget;